import React, { useState, useEffect, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { toastActions } from '../../../components/uielements/toast/toast';
import { AdminLayout } from '../adminLayout';
import { IntlMessages } from '../../../helper/langHelper';
import {
  Container,
  HeaderSection,
  PageTitle,
  SaveButton,
  HiddenJsonArea,
  QuestionTreeContainer,
  QuestionNode,
  QuestionNodeHeader,
  ToggleButton,
  QuestionTitle,
  ActionButtonsGroup,
  EditButton,
  AddOptionButton,
  DeleteButton,
  QuestionContent,
  QuestionText,
  OptionsList,
  OptionItem,
  OptionText,
  ClusterBadge,
  NestedQuestionsContainer,
  Modal,
  ModalContent,
  ModalTitle,
  FormGroup,
  FormLabel,
  FormInput,
  FormTextarea,
  FormSelect,
  ModalButtonGroup,
  PrimaryModalButton,
  SecondaryModalButton,
  LoadingContainer,
  LoadingSpinner,
  LoadingText,
  ErrorContainer,
  ConditionalFieldWrapper,
} from './Clusterquestionscreation.style';
import {
  UploadBox,
  HiddenFileInput,
  UploadText,
  UploadedFileCard,
  FileInfoRow,
  FileName,
  FileActions,
  ActionButton,
  HelperText,
  UploadingText,
  LinksSection,
  LinkItem,
  LinkInputLabel,
  LinkInput,
  AddMoreButton,
} from '../clusters/Clusters.styles';

import {
  listOfClusterQuestions,
  questionAddedToCluster,
} from '../../../services/admin/clusterQuestionCreation';
import { saveFile, deleteFile, viewFile } from '../../../services/admin/files';
import { ChevronDown, Edit2, Plus, Trash2, X } from 'lucide-react';
import { getClusters } from '../../../services/admin/clusters';
import { UploadIcon } from '../../../icons';

const QUESTION_TYPES = [
  { label: 'Text', value: 'TEXT' },
  { label: 'Option', value: 'OPTION' },
  { label: 'Number', value: 'NUMBER' },
  { label: 'Impact', value: 'IMPACT' },
];

const FLOW_TYPES = [
  { label: 'New Question', value: 'NQ' },
  { label: 'Re-loop Question', value: 'RL' },
  { label: 'Insert Cluster', value: 'IC' },
];

const ALLOWED_FILE_TYPES = ['image/jpg', 'image/jpeg', 'image/png', 'image/webp'];
const MAX_FILE_SIZE = 5 * 1024 * 1024; // 5MB

const generateRandomCode = () => Math.floor(Math.random() * 900000 + 100000).toString();
const generateQuestionCode = (parentCode) => `${parentCode}${Math.floor(Math.random() * 90 + 10)}`;

// ─── Helper: normalise clusterId so it's always an array ──────────────────────
const normaliseClusters = (clusterId) => {
  if (!clusterId) return [];
  if (Array.isArray(clusterId)) return clusterId;
  if (typeof clusterId === 'object') return [clusterId];
  return [];
};

// ─── Reusable file upload hook with document management ─────────────────────────
const useFileUpload = (initialDocuments = []) => {
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [existingDocuments, setExistingDocuments] = useState(initialDocuments || []);
  const [isUploadingFile, setIsUploadingFile] = useState(false);
  const [fileError, setFileError] = useState('');
  const [deleteCallbackFn, setDeleteCallbackFn] = useState(null);

  const fileUploadMutation = useMutation({
    mutationFn: (payload) => saveFile(payload),
    onSuccess: (response, payload) => {
      const newFile = {
        documentId: response?.data?.documentId || response?.documentId,
        fileName: response?.data?.fileName || response?.fileName || payload?.fileName,
        contentType: response?.data?.contentType || response?.contentType,
        isNew: true,
      };
      setUploadedFiles((prev) => [...prev, newFile]);
      setFileError('');
      toastActions.success(
        <IntlMessages id="toast.file.upload.success" defaultMessage="File uploaded successfully." />
      );
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to upload file. Please try again.';
      setFileError(errorMessage);
      toastActions.error(
        <IntlMessages id="toast.file.upload.error" defaultMessage="Failed to upload file." />
      );
    },
  });

  const fileDeleteMutation = useMutation({
    mutationFn: (payload) => deleteFile(payload),
    onSuccess: (_, variables) => {
      // After successful API deletion, remove from state and trigger callback
      setUploadedFiles((prev) => prev.filter((f) => f.documentId !== variables.documentId));
      setExistingDocuments((prev) => prev.filter((d) => d.documentId !== variables.documentId));

      // Call the callback function if provided (to update parent state)
      if (deleteCallbackFn) {
        deleteCallbackFn(variables.documentId);
      }

      setFileError('');
      toastActions.success(
        <IntlMessages id="toast.file.delete.success" defaultMessage="File deleted successfully." />
      );
    },
    onError: (error) => {
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        'Failed to delete file. Please try again.';
      setFileError(errorMessage);
      toastActions.error(
        <IntlMessages id="toast.file.delete.error" defaultMessage="Failed to delete file." />
      );
    },
  });

  const fileViewMutation = useMutation({
    mutationFn: (payload) => viewFile(payload),
    onSuccess: (response) => {
      if (response?.documentUrl) {
        window.open(response.documentUrl, '_blank');
      }
    },
    onError: () => {
      toastActions.error(
        <IntlMessages id="toast.file.view.error" defaultMessage="Failed to view file." />
      );
    },
  });

  const isFileOperationLoading =
    fileUploadMutation.isPending || fileDeleteMutation.isPending || fileViewMutation.isPending;

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setFileError('');

    if (!ALLOWED_FILE_TYPES.includes(file.type)) {
      setFileError('Only JPG, JPEG, PNG, and WebP files are allowed.');
      return;
    }
    if (file.size > MAX_FILE_SIZE) {
      setFileError('File size must not exceed 5MB.');
      return;
    }

    const reader = new FileReader();
    reader.onload = (event) => {
      const base64String = event.target?.result?.split(',')[1];
      const contentType = file.type;
      if (base64String) {
        setIsUploadingFile(true);
        fileUploadMutation.mutate(
          { file: base64String, contentType, fileName: file.name },
          {
            onSettled: () => {
              setIsUploadingFile(false);
              e.target.value = '';
            },
          }
        );
      }
    };
    reader.onerror = () => setFileError('Failed to read file. Please try again.');
    reader.readAsDataURL(file);
  };

  // NEW: Delete with API call first
  const handleFileDelete = (documentId) => {
    fileDeleteMutation.mutate({ documentId });
  };

  const handleFileView = (documentId) => {
    fileViewMutation.mutate({ documentId });
  };

  // UPDATED: Now calls API first via fileDeleteMutation
  const handleExistingDocumentDelete = (documentId, onDeleteCallback) => {
    // Set callback function to handle parent state update
    setDeleteCallbackFn(() => onDeleteCallback);
    // Call API delete mutation
    fileDeleteMutation.mutate({ documentId });
  };

  const getAllDocuments = () => {
    return [...existingDocuments, ...uploadedFiles];
  };

  const resetFiles = () => {
    setUploadedFiles([]);
    setFileError('');
    setIsUploadingFile(false);
  };

  const initializeWithDocuments = (docs) => {
    setExistingDocuments(docs || []);
  };

  return {
    uploadedFiles,
    existingDocuments,
    isUploadingFile,
    fileError,
    isFileOperationLoading,
    handleFileUpload,
    handleFileDelete,
    handleFileView,
    handleExistingDocumentDelete,
    getAllDocuments,
    resetFiles,
    initializeWithDocuments,
  };
};

// ─── Reusable FileUploadSection component with existing documents ─────────────────
const FileUploadSection = ({
  uploadedFiles,
  existingDocuments,
  isUploadingFile,
  fileError,
  isFileOperationLoading,
  isLoading,
  handleFileUpload,
  handleFileDelete,
  handleFileView,
  handleExistingDocumentDelete,
  inputId = 'modal-file-upload',
}) => {
  const allDocuments = [...existingDocuments, ...uploadedFiles];

  return (
    <FormGroup>
      <FormLabel>Upload Files (Optional)</FormLabel>

      {fileError && (
        <div
          style={{
            backgroundColor: '#ffebee',
            color: '#c62828',
            padding: '8px 12px',
            borderRadius: '4px',
            marginBottom: '12px',
            fontSize: '13px',
            border: '1px solid #ef5350',
          }}
        >
          {fileError}
        </div>
      )}

      {/* Uploaded files list */}
      {allDocuments.map((file) => {
        const isExisting = existingDocuments.some((d) => d.documentId === file.documentId);
        const fileName = file.fileName || file.documentId;

        return (
          <UploadedFileCard key={file.documentId} style={{ marginBottom: '8px' }}>
            <FileInfoRow>
              <FileName>
                {isExisting ? '📄' : '✅'} {fileName}
              </FileName>
              <FileActions>
                <ActionButton
                  type="button"
                  onClick={() => handleFileView(file.documentId)}
                  disabled={isFileOperationLoading || isLoading}
                >
                  View
                </ActionButton>
                <ActionButton
                  type="button"
                  variant="delete"
                  onClick={() => {
                    // Both existing and new files now use API-first delete
                    handleFileDelete(file.documentId);
                  }}
                  disabled={isFileOperationLoading || isLoading}
                >
                  Delete
                </ActionButton>
              </FileActions>
            </FileInfoRow>
          </UploadedFileCard>
        );
      })}

      {/* Upload box — always visible so more files can be added */}
      <UploadBox htmlFor={inputId} style={{ marginTop: allDocuments.length ? '8px' : 0 }}>
        <UploadIcon style={{ width: '30px', height: '30px' }} />
        <UploadText>{allDocuments.length > 0 ? 'Upload another file' : 'Upload files'}</UploadText>
        <HiddenFileInput
          id={inputId}
          type="file"
          accept="image/jpg,image/jpeg,image/png,image/webp"
          onChange={handleFileUpload}
          disabled={isLoading || isFileOperationLoading || isUploadingFile}
        />
      </UploadBox>

      {isUploadingFile && <UploadingText>Uploading file...</UploadingText>}

      <HelperText>
        Allowed formats: JPG, JPEG, PNG, WebP (Max 5MB each)
        {allDocuments.length > 0 && ` · ${allDocuments.length} file(s) added`}
      </HelperText>
    </FormGroup>
  );
};

const ClusterQuestionsCreation = () => {
  const { clusterId } = useParams();
  const queryClient = useQueryClient();
  const jsonAreaRef = useRef(null);

  // ─── State ────────────────────────────────────────────────────────────────
  const [questionList, setQuestionList] = useState([]);
  const [expandedQuestions, setExpandedQuestions] = useState({});
  const [clusterList, setClusterList] = useState([]);

  // Modal visibility
  const [showEditQuestionModal, setShowEditQuestionModal] = useState(false);
  const [showAddOptionModal, setShowAddOptionModal] = useState(false);
  const [showAddPromptsModal, setShowAddPromptsModal] = useState(false);
  const [showEditPromptsModal, setShowEditPromptsModal] = useState(false);
  const [showEditOptionModal, setShowEditOptionModal] = useState(false);
  const [showAddQuestionModal, setShowAddQuestionModal] = useState(false);

  // Form data
  const [editingQuestion, setEditingQuestion] = useState(null);
  const [editingOption, setEditingOption] = useState(null);
  const [newOptionInputs, setNewOptionInputs] = useState([]);
  const [newPromptInputs, setNewPromptInputs] = useState([]);
  const [parentQuestionCode, setParentQuestionCode] = useState(null);
  const [parentQuestion, setParentQuestion] = useState(null);
  const [selectedClusterForEdit, setSelectedClusterForEdit] = useState('');
  const [editOptionClusters, setEditOptionClusters] = useState([]);

  // Add Question Modal state
  const [addQuestionData, setAddQuestionData] = useState({
    flowType: 'NQ',
    newQuestionTitle: '',
    newQuestion: '',
    questionType: 'OPTION',
    newValidationPrompt: '',
    questionName: '',
    questionName2: 'null',
    clusterName: '',
    optionCode: '',
    newQuestionCode: '',
    parentQuestionCode: '',
  });

  // Edit Question Modal state
  const [editingQuestionData, setEditingQuestionData] = useState({
    title: '',
    question: '',
    questionType: 'TEXT',
    validationPrompt: '',
    linkArray: [],
  });

  // ─── File upload hooks (one per modal that needs it) ──────────────────────
  const editQuestionFiles = useFileUpload();
  const addQuestionFiles = useFileUpload();

  // ─── Queries ──────────────────────────────────────────────────────────────
  const {
    data: questionsData,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['clusterQuestions', clusterId],
    queryFn: () => listOfClusterQuestions({ clusterId }),
  });

  const { data: clustersData } = useQuery({
    queryKey: ['adminClusters', 0, 1000],
    queryFn: () => getClusters(0, 1000),
    enabled: true,
    staleTime: 5 * 60 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  useEffect(() => {
    const raw = Array.isArray(questionsData)
      ? questionsData
      : questionsData?.data && Array.isArray(questionsData.data)
        ? questionsData.data
        : [];

    const normalised = raw.map((q) => ({
      ...q,
      nextQuestion: (q.nextQuestion || []).map((opt) => ({
        ...opt,
        clusterId: normaliseClusters(opt.clusterId),
      })),
    }));
    setQuestionList(normalised);
  }, [questionsData]);

  useEffect(() => {
    if (clustersData) setClusterList(clustersData?.data || []);
  }, [clustersData]);

  const saveMutation = useMutation({
    mutationFn: (payload) => questionAddedToCluster(payload),
    onSuccess: () => {
      toastActions.success(
        <IntlMessages
          id="toast.questions.save.success"
          defaultMessage="Questions saved successfully."
        />
      );
      queryClient.invalidateQueries({ queryKey: ['clusterQuestions', clusterId] });
    },
    onError: (error) => {
      toastActions.error(
        <IntlMessages id="toast.questions.save.error" defaultMessage="Failed to save questions." />
      );
      console.error('Save error:', error);
    },
  });

  useEffect(() => {
    if (jsonAreaRef.current) {
      jsonAreaRef.current.value = JSON.stringify(questionList, null, 4);
    }
  }, [questionList]);

  // ─── Toggle expansion ──────────────────────────────────────────────────────
  const toggleQuestionExpand = (questionCode) => {
    setExpandedQuestions((prev) => ({ ...prev, [questionCode]: !prev[questionCode] }));
  };

  // ─── Helper: does this question type show validation prompt? ───────────────
  const isNonOptionType = (type) => type !== 'OPTION';

  // ─── Edit Question ────────────────────────────────────────────────────────
  const handleEditQuestion = (question) => {
    setEditingQuestion(question);
    setEditingQuestionData({
      title: question.title,
      question: question.question,
      questionType: question.questionType,
      validationPrompt: question.validationPrompt || '',
      linkArray: question.linkArray || [],
    });
    // Initialize with existing documents from question
    const existingDocs = question.documentArray
      ? question.documentArray.map((doc) => ({
          documentId: doc.documentId,
          fileName: doc.documentId,
          contentType: 'image',
          isNew: false,
        }))
      : [];
    editQuestionFiles.initializeWithDocuments(existingDocs);
    editQuestionFiles.resetFiles();
    setShowEditQuestionModal(true);
  };

  const handleSaveEditQuestion = () => {
    if (
      !editingQuestion ||
      !editingQuestionData.title.trim() ||
      !editingQuestionData.question.trim()
    ) {
      toastActions.error('Please fill in all required fields.');
      return;
    }

    // Combine existing documents and newly uploaded files
    const allDocs = editQuestionFiles.getAllDocuments();
    const documentArray =
      allDocs.length > 0 ? allDocs.map((f) => ({ documentId: f.documentId })) : undefined;

    setQuestionList((prev) =>
      prev.map((q) =>
        q.questionCode === editingQuestion.questionCode
          ? {
              ...q,
              ...editingQuestionData,
              ...(documentArray ? { documentArray } : { documentArray: [] }),
              ...(editingQuestionData.linkArray.length > 0 && {
                linkArray: editingQuestionData.linkArray,
              }),
            }
          : q
      )
    );
    setShowEditQuestionModal(false);
    setEditingQuestion(null);
    editQuestionFiles.resetFiles();
    toastActions.success('Question updated successfully.');
  };

  // ─── Handle link operations in edit question ──────────────────────────────
  const handleAddLinkInEditQuestion = () => {
    setEditingQuestionData((prev) => ({
      ...prev,
      linkArray: [...(prev.linkArray || []), { title: '', link: '' }],
    }));
  };

  const handleLinkChangeInEditQuestion = (index, field, value) => {
    setEditingQuestionData((prev) => ({
      ...prev,
      linkArray: (prev.linkArray || []).map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      ),
    }));
  };

  const handleDeleteLinkInEditQuestion = (index) => {
    setEditingQuestionData((prev) => ({
      ...prev,
      linkArray: (prev.linkArray || []).filter((_, i) => i !== index),
    }));
  };

  // ─── Add Question ─────────────────────────────────────────────────────────
  const openAddQuestionModal = (optionId, parentQCode) => {
    setAddQuestionData((prev) => ({
      ...prev,
      optionCode: optionId,
      parentQuestionCode: parentQCode,
      newQuestionCode: generateQuestionCode(parentQCode),
      flowType: 'NQ',
      newQuestionTitle: '',
      newQuestion: '',
      questionType: 'OPTION',
      newValidationPrompt: '',
      questionName: '',
      questionName2: 'null',
      clusterName: '',
    }));
    addQuestionFiles.resetFiles();
    addQuestionFiles.initializeWithDocuments([]);
    setShowAddQuestionModal(true);
  };

  const handleFlowTypeChange = (flowType) => {
    const sorted = [...questionList].sort((a, b) => a.title.localeCompare(b.title));
    if (flowType === 'NQ') {
      addQuestionFiles.resetFiles();
      addQuestionFiles.initializeWithDocuments([]);
      setAddQuestionData((prev) => ({ ...prev, flowType: 'NQ' }));
    } else if (flowType === 'RL') {
      addQuestionFiles.resetFiles();
      addQuestionFiles.initializeWithDocuments([]);
      setAddQuestionData((prev) => ({
        ...prev,
        flowType: 'RL',
        questionName: sorted[0]?.questionCode || '',
      }));
    } else if (flowType === 'IC') {
      addQuestionFiles.resetFiles();
      addQuestionFiles.initializeWithDocuments([]);
      setAddQuestionData((prev) => ({
        ...prev,
        flowType: 'IC',
        clusterName: clusterList[0]?._id || '',
        questionName2: 'null',
      }));
    }
  };

  const handleAddClusterToEditOption = () => {
    setEditOptionClusters((prev) => [...prev, { _id: '', name: '', questionCode: null, id: '' }]);
  };

  const handleRemoveClusterFromEditOption = (index) => {
    setEditOptionClusters((prev) => prev.filter((_, i) => i !== index));
  };

  const handleUpdateClusterInEditOption = (index, clusterId) => {
    const selectedCluster = clusterList.find((c) => c._id === clusterId);
    setEditOptionClusters((prev) =>
      prev.map((cluster, i) =>
        i === index
          ? {
              _id: selectedCluster?._id || clusterId,
              name: selectedCluster?.name || '',
              questionCode: null,
              id: selectedCluster?._id || clusterId,
            }
          : cluster
      )
    );
  };

  const handleSaveQuestion = () => {
    const { flowType, optionCode, newQuestionCode } = addQuestionData;
    if (!optionCode || !newQuestionCode) {
      toastActions.error('Invalid option or question code.');
      return;
    }

    // Build documentArray for NQ payloads
    const allDocs = addQuestionFiles.getAllDocuments();
    const documentArray =
      allDocs.length > 0 && flowType === 'NQ'
        ? allDocs.map((f) => ({ documentId: f.documentId }))
        : undefined;

    if (flowType === 'NQ') {
      const { newQuestion, newQuestionTitle, questionType, newValidationPrompt } = addQuestionData;
      if (!newQuestion.trim() || !newQuestionTitle.trim()) {
        toastActions.error('Please fill in all required fields.');
        return;
      }
      setQuestionList((prev) => [
        ...prev,
        {
          title: newQuestionTitle,
          question: newQuestion,
          questionCode: newQuestionCode,
          questionType,
          validationPrompt: questionType === 'OPTION' ? null : newValidationPrompt,
          clusterId,
          nextQuestion: [],
          ...(documentArray ? { documentArray } : { documentArray: [] }),
        },
      ]);
      setQuestionList((prev) =>
        prev.map((q) => ({
          ...q,
          nextQuestion: (q.nextQuestion || []).map((opt) =>
            opt.optionId === optionCode ? { ...opt, questionCode: newQuestionCode, end: 0 } : opt
          ),
        }))
      );
    } else if (flowType === 'RL') {
      const { questionName } = addQuestionData;
      setQuestionList((prev) =>
        prev.map((q) => ({
          ...q,
          nextQuestion: (q.nextQuestion || []).map((opt) =>
            opt.optionId === optionCode ? { ...opt, questionCode: questionName, end: 1 } : opt
          ),
        }))
      );
    } else if (flowType === 'IC') {
      const { clusterName, questionName2 } = addQuestionData;
      const selectedCluster = clusterList.find((c) => c._id === clusterName);

      setQuestionList((prev) =>
        prev.map((q) => ({
          ...q,
          nextQuestion: (q.nextQuestion || []).map((opt) => {
            if (opt.optionId !== optionCode) return opt;

            const existingClusters = normaliseClusters(opt.clusterId);
            const alreadyLinked = existingClusters.find((c) => c._id === clusterName);
            const updatedClusters = alreadyLinked
              ? existingClusters
              : [
                  ...existingClusters,
                  {
                    _id: selectedCluster?._id || clusterName,
                    name: selectedCluster?.name || '',
                    questionCode:
                      questionName2 !== 'null' && questionName2 !== '1' ? questionName2 : null,
                    id: selectedCluster?._id || clusterName,
                  },
                ];

            let updated = { ...opt, clusterId: updatedClusters };

            if (questionName2 === '1') {
              const { newQuestion, newQuestionTitle, questionType, newValidationPrompt } =
                addQuestionData;
              if (!newQuestion.trim() || !newQuestionTitle.trim()) {
                toastActions.error('Please fill in all required fields.');
                return opt;
              }
              updated = { ...updated, questionCode: newQuestionCode, end: 0 };
            } else if (questionName2 && questionName2 !== 'null') {
              updated = { ...updated, questionCode: questionName2, end: 1 };
            }

            return updated;
          }),
        }))
      );

      if (addQuestionData.questionName2 === '1') {
        const { newQuestion, newQuestionTitle, questionType, newValidationPrompt } =
          addQuestionData;
        if (newQuestion.trim() && newQuestionTitle.trim()) {
          setQuestionList((prev) => [
            ...prev,
            {
              title: newQuestionTitle,
              question: newQuestion,
              questionCode: newQuestionCode,
              questionType,
              validationPrompt: questionType === 'OPTION' ? null : newValidationPrompt,
              clusterId,
              nextQuestion: [],
            },
          ]);
        }
      }
    }

    setShowAddQuestionModal(false);
    addQuestionFiles.resetFiles();
    toastActions.success('Question added successfully.');
  };

  // ─── Add Options / Prompts Modal ───────────────────────────────────────────
  const handleAddOptionButtonClick = (questionCode, question) => {
    setParentQuestionCode(questionCode);
    setParentQuestion(question);

    if (question.questionType === 'OPTION') {
      setNewOptionInputs([{ id: Math.random(), value: '' }]);
      setShowAddOptionModal(true);
    } else {
      // TEXT, NUMBER, IMPACT — show prompts modal
      setNewPromptInputs([{ id: Math.random(), value: '' }]);
      setShowAddPromptsModal(true);
    }
  };

  const handleSaveOptions = () => {
    if (!parentQuestionCode) return;
    const validOptions = newOptionInputs.filter((opt) => opt.value.trim());
    if (validOptions.length === 0) {
      toastActions.error('Please add at least one option.');
      return;
    }
    setQuestionList((prev) =>
      prev.map((q) =>
        q.questionCode === parentQuestionCode
          ? {
              ...q,
              nextQuestion: [
                ...(q.nextQuestion || []),
                ...validOptions.map((opt) => ({
                  option: opt.value.trim(),
                  questionCode: null,
                  optionId: generateRandomCode(),
                  clusterId: [],
                  end: 0,
                })),
              ],
            }
          : q
      )
    );
    setShowAddOptionModal(false);
    setNewOptionInputs([]);
    setParentQuestionCode(null);
    setParentQuestion(null);
    toastActions.success('Options added successfully.');
  };

  const handleSavePrompts = () => {
    if (!parentQuestionCode) return;
    const validPrompts = newPromptInputs.filter((p) => p.value.trim());
    if (validPrompts.length === 0) {
      toastActions.error('Please add at least one prompt.');
      return;
    }
    setQuestionList((prev) =>
      prev.map((q) =>
        q.questionCode === parentQuestionCode
          ? {
              ...q,
              nextQuestion: [
                ...(q.nextQuestion || []),
                ...validPrompts.map((p) => ({
                  prompt: p.value.trim(),
                  option: p.value.trim(),
                  questionCode: null,
                  optionId: generateRandomCode(),
                  clusterId: [],
                  end: 0,
                })),
              ],
            }
          : q
      )
    );
    setShowAddPromptsModal(false);
    setNewPromptInputs([]);
    setParentQuestionCode(null);
    setParentQuestion(null);
    toastActions.success('Prompts added successfully.');
  };

  // ─── Edit Option ──────────────────────────────────────────────────────────
  const handleEditOption = (questionCode, option) => {
    setParentQuestionCode(questionCode);
    const normalizedClusters = normaliseClusters(option.clusterId);
    setEditingOption({ ...option, clusterId: normalizedClusters });
    setEditOptionClusters(normalizedClusters);
    const question = questionList.find((q) => q.questionCode === questionCode);
    if (question?.questionType !== 'OPTION') {
      setShowEditPromptsModal(true);
    } else {
      setShowEditOptionModal(true);
    }
  };

  const handleSaveEditOption = () => {
    if (!editingOption || !editingOption.option?.trim()) {
      toastActions.error('Please fill in the option.');
      return;
    }
    setQuestionList((prev) =>
      prev.map((q) =>
        q.questionCode === parentQuestionCode
          ? {
              ...q,
              nextQuestion: (q.nextQuestion || []).map((opt) =>
                opt.optionId === editingOption.optionId
                  ? { ...opt, option: editingOption.option, clusterId: editOptionClusters }
                  : opt
              ),
            }
          : q
      )
    );
    setShowEditOptionModal(false);
    setEditingOption(null);
    setEditOptionClusters([]);
    toastActions.success('Option updated successfully.');
  };

  const handleSaveEditPrompts = () => {
    if (
      !editingOption ||
      (editingOption.prompt?.trim() === '' && editingOption.option?.trim() === '')
    ) {
      toastActions.error('Please fill in the prompt.');
      return;
    }
    const promptValue = editingOption.prompt?.trim() || editingOption.option?.trim();

    setQuestionList((prev) =>
      prev.map((q) =>
        q.questionCode === parentQuestionCode
          ? {
              ...q,
              nextQuestion: (q.nextQuestion || []).map((opt) =>
                opt.optionId === editingOption.optionId
                  ? {
                      ...opt,
                      prompt: promptValue,
                      option: promptValue,
                      clusterId: editOptionClusters,
                    }
                  : opt
              ),
            }
          : q
      )
    );
    setShowEditPromptsModal(false);
    setEditingOption(null);
    setEditOptionClusters([]);
    toastActions.success('Prompt updated successfully.');
  };

  // ─── Delete Option ────────────────────────────────────────────────────────
  const handleDeleteOption = (questionCode, optionId) => {
    if (window.confirm('Are you sure you want to delete this option and all nested questions?')) {
      setQuestionList((prev) =>
        prev.map((q) =>
          q.questionCode === questionCode
            ? {
                ...q,
                nextQuestion: (q.nextQuestion || []).filter((opt) => opt.optionId !== optionId),
              }
            : q
        )
      );
      toastActions.success('Option deleted successfully.');
    }
  };

  // ─── Save All ─────────────────────────────────────────────────────────────
  const handleSaveAll = () => {
    if (questionList.length === 0) {
      toastActions.error('Please add at least one question.');
      return;
    }
    saveMutation.mutate({ clusterId, questionArray: questionList });
  };

  // ─── Render helpers ───────────────────────────────────────────────────────
  const resolveClusterName = (clusterEntry) => {
    if (!clusterEntry) return 'Cluster';
    if (clusterEntry.name) return clusterEntry.name;
    const found = clusterList.find((c) => c._id === clusterEntry._id);
    return found?.name || 'Cluster';
  };

  const resolveQuestionTitle = (questionCode) => {
    if (!questionCode) return '';
    const found = questionList.find((q) => q.questionCode === questionCode);
    return found?.title || 'Unknown Question';
  };

  // ─── Render Question Node ─────────────────────────────────────────────────
  const renderQuestionNode = (question, level = 0) => {
    const isExpanded = expandedQuestions[question.questionCode];
    const hasOptions = question.nextQuestion && question.nextQuestion.length > 0;

    return (
      <div key={question.questionCode} style={{ marginLeft: `${level * 20}px` }}>
        <QuestionNode>
          <QuestionNodeHeader>
            {hasOptions && (
              <ToggleButton
                isOpen={isExpanded}
                onClick={() => toggleQuestionExpand(question.questionCode)}
              >
                <ChevronDown size={16} />
              </ToggleButton>
            )}
            <div style={{ flex: 1 }}>
              <QuestionTitle>{question.title}</QuestionTitle>
              <QuestionText>{question.question}</QuestionText>
              <span
                style={{ fontSize: '12px', color: '#9ca3af', marginTop: '4px', display: 'block' }}
              >
                Type: {question.questionType}
              </span>
            </div>
            <ActionButtonsGroup>
              <EditButton onClick={() => handleEditQuestion(question)} title="Edit Question">
                <Edit2 size={14} />
              </EditButton>
              <AddOptionButton
                onClick={() => handleAddOptionButtonClick(question.questionCode, question)}
                title={question.questionType === 'OPTION' ? 'Add Option' : 'Add Prompt'}
              >
                <Plus size={14} />
              </AddOptionButton>
            </ActionButtonsGroup>
          </QuestionNodeHeader>

          {isExpanded && hasOptions && (
            <QuestionContent>
              <OptionsList>
                {question.nextQuestion.map((option) => {
                  const clusters = normaliseClusters(option.clusterId);
                  const displayText = option.option || option.prompt;
                  const isPrompt = option.prompt !== undefined;

                  return (
                    <OptionItem key={option.optionId}>
                      <div
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'flex-start',
                          gap: '8px',
                        }}
                      >
                        <div style={{ flex: 1 }}>
                          <OptionText>{displayText}</OptionText>

                          {clusters.length > 0 && (
                            <div
                              style={{
                                display: 'flex',
                                flexWrap: 'wrap',
                                gap: '4px',
                                marginTop: '4px',
                              }}
                            >
                              {clusters.map((clusterEntry) => (
                                <ClusterBadge
                                  key={clusterEntry._id}
                                  style={{ display: 'flex', alignItems: 'center', gap: '4px' }}
                                >
                                  {resolveClusterName(clusterEntry)}
                                  <button
                                    type="button"
                                    title="Remove cluster"
                                    onClick={() => {
                                      setQuestionList((prev) =>
                                        prev.map((q) =>
                                          q.questionCode === question.questionCode
                                            ? {
                                                ...q,
                                                nextQuestion: (q.nextQuestion || []).map((opt) =>
                                                  opt.optionId === option.optionId
                                                    ? {
                                                        ...opt,
                                                        clusterId: normaliseClusters(
                                                          opt.clusterId
                                                        ).filter((c) => c._id !== clusterEntry._id),
                                                      }
                                                    : opt
                                                ),
                                              }
                                            : q
                                        )
                                      );
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0 2px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: 'inherit',
                                    }}
                                  >
                                    <X size={10} />
                                  </button>
                                </ClusterBadge>
                              ))}
                            </div>
                          )}

                          {option.questionCode &&
                            option.questionCode !== 'null' &&
                            option.end === 1 && (
                              <div style={{ marginTop: '4px' }}>
                                <ClusterBadge
                                  style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    background: 'linear-gradient(90deg, #3b82f6, #2563eb)',
                                    color: '#fff',
                                  }}
                                >
                                  ↩️ {resolveQuestionTitle(option.questionCode)}
                                  <button
                                    type="button"
                                    title="Remove re-loop"
                                    onClick={() => {
                                      setQuestionList((prev) =>
                                        prev.map((q) =>
                                          q.questionCode === question.questionCode
                                            ? {
                                                ...q,
                                                nextQuestion: (q.nextQuestion || []).map((opt) =>
                                                  opt.optionId === option.optionId
                                                    ? { ...opt, questionCode: null, end: 0 }
                                                    : opt
                                                ),
                                              }
                                            : q
                                        )
                                      );
                                    }}
                                    style={{
                                      background: 'none',
                                      border: 'none',
                                      cursor: 'pointer',
                                      padding: '0 2px',
                                      display: 'flex',
                                      alignItems: 'center',
                                      color: 'inherit',
                                    }}
                                  >
                                    <X size={10} />
                                  </button>
                                </ClusterBadge>
                              </div>
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: '4px', flexShrink: 0 }}>
                          {!option.questionCode && (
                            <AddOptionButton
                              onClick={() =>
                                openAddQuestionModal(option.optionId, question.questionCode)
                              }
                              title="Add Question"
                              style={{ width: '24px', height: '24px' }}
                            >
                              <Plus size={12} />
                            </AddOptionButton>
                          )}
                          <EditButton
                            onClick={() => handleEditOption(question.questionCode, option)}
                            title={isPrompt ? 'Edit Prompt' : 'Edit Option'}
                            style={{ width: '24px', height: '24px' }}
                          >
                            <Edit2 size={12} />
                          </EditButton>
                          <DeleteButton
                            onClick={() =>
                              handleDeleteOption(question.questionCode, option.optionId)
                            }
                            title="Delete"
                            style={{ width: '24px', height: '24px' }}
                          >
                            <Trash2 size={12} />
                          </DeleteButton>
                        </div>
                      </div>

                      {/* Nested question - only show for new questions (end === 0), not for re-loops */}
                      {isExpanded &&
                        option.questionCode &&
                        option.questionCode !== 'null' &&
                        option.end === 0 && (
                          <NestedQuestionsContainer>
                            {questionList
                              .filter((q) => q.questionCode === option.questionCode)
                              .map((q) => renderQuestionNode(q, 1))}
                          </NestedQuestionsContainer>
                        )}
                    </OptionItem>
                  );
                })}
              </OptionsList>
            </QuestionContent>
          )}
        </QuestionNode>
      </div>
    );
  };

  // ─── Modal: Edit Question ─────────────────────────────────────────────────
  const renderEditQuestionModal = () => {
    if (!showEditQuestionModal) return null;
    const showValidationPrompt = isNonOptionType(editingQuestionData.questionType);

    return (
      <Modal onClick={() => setShowEditQuestionModal(false)}>
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Edit Question</ModalTitle>
            <button
              onClick={() => setShowEditQuestionModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>

          <FormGroup>
            <FormLabel>Title *</FormLabel>
            <FormInput
              value={editingQuestionData.title}
              onChange={(e) =>
                setEditingQuestionData({ ...editingQuestionData, title: e.target.value })
              }
              placeholder="Enter question title"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Question *</FormLabel>
            <FormTextarea
              value={editingQuestionData.question}
              onChange={(e) =>
                setEditingQuestionData({ ...editingQuestionData, question: e.target.value })
              }
              placeholder="Enter question text"
            />
          </FormGroup>

          <FormGroup>
            <FormLabel>Question Type *</FormLabel>
            <FormSelect
              value={editingQuestionData.questionType}
              onChange={(e) =>
                setEditingQuestionData({ ...editingQuestionData, questionType: e.target.value })
              }
            >
              {QUESTION_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          <ConditionalFieldWrapper visible={showValidationPrompt}>
            <FormGroup>
              <FormLabel>Validation Prompt</FormLabel>
              <FormTextarea
                value={editingQuestionData.validationPrompt}
                onChange={(e) =>
                  setEditingQuestionData({
                    ...editingQuestionData,
                    validationPrompt: e.target.value,
                  })
                }
                placeholder="Enter validation prompt"
              />
            </FormGroup>
          </ConditionalFieldWrapper>

          {/* ── File Upload with existing documents ── */}
          <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
            <FileUploadSection
              inputId="edit-question-file-upload"
              uploadedFiles={editQuestionFiles.uploadedFiles}
              existingDocuments={editQuestionFiles.existingDocuments}
              isUploadingFile={editQuestionFiles.isUploadingFile}
              fileError={editQuestionFiles.fileError}
              isFileOperationLoading={editQuestionFiles.isFileOperationLoading}
              isLoading={false}
              handleFileUpload={editQuestionFiles.handleFileUpload}
              handleFileDelete={editQuestionFiles.handleFileDelete}
              handleFileView={editQuestionFiles.handleFileView}
              handleExistingDocumentDelete={editQuestionFiles.handleExistingDocumentDelete}
            />
          </div>

          {/* ── Links Section ── */}
          <FormGroup
            style={{ marginTop: '16px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}
          >
            <FormLabel>Add Links (Optional)</FormLabel>
            <LinksSection>
              {editingQuestionData.linkArray && editingQuestionData.linkArray.length > 0 && (
                <div style={{ marginBottom: '12px' }}>
                  {editingQuestionData.linkArray.map((item, index) => (
                    <LinkItem key={index}>
                      <div>
                        <LinkInputLabel>Link Title</LinkInputLabel>
                        <LinkInput
                          type="text"
                          value={item.title}
                          onChange={(e) =>
                            handleLinkChangeInEditQuestion(index, 'title', e.target.value)
                          }
                          placeholder="e.g., Documentation"
                        />
                      </div>
                      <div>
                        <LinkInputLabel>URL</LinkInputLabel>
                        <LinkInput
                          type="url"
                          value={item.link}
                          onChange={(e) =>
                            handleLinkChangeInEditQuestion(index, 'link', e.target.value)
                          }
                          placeholder="e.g., https://example.com"
                        />
                      </div>
                      <DeleteButton
                        type="button"
                        onClick={() => handleDeleteLinkInEditQuestion(index)}
                      >
                        <Trash2 size={14} />
                      </DeleteButton>
                    </LinkItem>
                  ))}
                </div>
              )}
              <AddMoreButton type="button" onClick={handleAddLinkInEditQuestion}>
                + Add More Link
              </AddMoreButton>
              <HelperText>
                {editingQuestionData.linkArray && editingQuestionData.linkArray.length > 0
                  ? `${editingQuestionData.linkArray.length} link(s) added`
                  : ''}
              </HelperText>
            </LinksSection>
          </FormGroup>

          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowEditQuestionModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton
              onClick={handleSaveEditQuestion}
              disabled={
                editQuestionFiles.isFileOperationLoading || editQuestionFiles.isUploadingFile
              }
            >
              Save Question
            </PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Modal: Add Options ───────────────────────────────────────────────────
  const renderAddOptionModal = () => {
    if (!showAddOptionModal) return null;
    return (
      <Modal onClick={() => setShowAddOptionModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Add Options</ModalTitle>
            <button
              onClick={() => setShowAddOptionModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ maxHeight: '300px', overflowY: 'auto', marginBottom: '16px' }}>
            {newOptionInputs.map((input, index) => (
              <FormGroup key={input.id}>
                <FormLabel>Option {index + 1} *</FormLabel>
                <FormInput
                  value={input.value}
                  onChange={(e) =>
                    setNewOptionInputs((prev) =>
                      prev.map((opt) =>
                        opt.id === input.id ? { ...opt, value: e.target.value } : opt
                      )
                    )
                  }
                  placeholder={`Enter option ${index + 1}`}
                />
              </FormGroup>
            ))}
          </div>
          <ModalButtonGroup>
            <SecondaryModalButton
              onClick={() =>
                setNewOptionInputs((prev) => [...prev, { id: Math.random(), value: '' }])
              }
            >
              Add More Option
            </SecondaryModalButton>
          </ModalButtonGroup>
          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowAddOptionModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton onClick={handleSaveOptions}>Save Options</PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Modal: Add Prompts ───────────────────────────────────────────────────
  const renderAddPromptsModal = () => {
    if (!showAddPromptsModal) return null;
    return (
      <Modal onClick={() => setShowAddPromptsModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Add Prompts</ModalTitle>
            <button
              onClick={() => setShowAddPromptsModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>
          <div style={{ maxHeight: '500px', overflowY: 'auto', marginBottom: '16px' }}>
            {newPromptInputs.map((input, index) => (
              <FormGroup key={input.id}>
                <FormLabel>Prompt {index + 1} *</FormLabel>
                <FormTextarea
                  value={input.value}
                  onChange={(e) =>
                    setNewPromptInputs((prev) =>
                      prev.map((p) => (p.id === input.id ? { ...p, value: e.target.value } : p))
                    )
                  }
                  placeholder={`Enter prompt ${index + 1}`}
                  style={{ minHeight: '80px' }}
                />
              </FormGroup>
            ))}
          </div>
          <ModalButtonGroup>
            <SecondaryModalButton
              onClick={() =>
                setNewPromptInputs((prev) => [...prev, { id: Math.random(), value: '' }])
              }
            >
              Add More Prompt
            </SecondaryModalButton>
          </ModalButtonGroup>
          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowAddPromptsModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton onClick={handleSavePrompts}>Save Prompts</PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Modal: Edit Option ───────────────────────────────────────────────────
  const renderEditOptionModal = () => {
    if (!showEditOptionModal || !editingOption) return null;
    return (
      <Modal onClick={() => setShowEditOptionModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Edit Option</ModalTitle>
            <button
              onClick={() => setShowEditOptionModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>
          <FormGroup>
            <FormLabel>Option Text *</FormLabel>
            <FormInput
              value={editingOption.option || editingOption.prompt || ''}
              onChange={(e) => setEditingOption({ ...editingOption, option: e.target.value })}
              placeholder="Enter option text"
            />
          </FormGroup>
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            <FormLabel style={{ display: 'block', marginBottom: '12px', fontWeight: '600' }}>
              Clusters
            </FormLabel>

            {editOptionClusters.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                {editOptionClusters.map((cluster, index) => (
                  <FormGroup key={index}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <FormSelect
                        value={cluster._id || ''}
                        onChange={(e) => handleUpdateClusterInEditOption(index, e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">-- Select Cluster --</option>
                        {clusterList.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </FormSelect>
                      <button
                        type="button"
                        onClick={() => handleRemoveClusterFromEditOption(index)}
                        style={{
                          padding: '8px 12px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </FormGroup>
                ))}
              </div>
            )}

            <button
              type="button"
              onClick={handleAddClusterToEditOption}
              style={{
                padding: '8px 12px',
                background: 'linear-gradient(90deg, #7b5cff, #60a5fa)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                width: '100%',
              }}
            >
              + Add Cluster
            </button>
          </div>
          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowEditOptionModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton onClick={handleSaveEditOption}>Save Option</PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Modal: Edit Prompts ──────────────────────────────────────────────────
  const renderEditPromptsModal = () => {
    if (!showEditPromptsModal || !editingOption) return null;
    return (
      <Modal onClick={() => setShowEditPromptsModal(false)}>
        <ModalContent onClick={(e) => e.stopPropagation()}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Edit Prompts</ModalTitle>
            <button
              onClick={() => setShowEditPromptsModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>
          <FormGroup>
            <FormLabel>Prompt *</FormLabel>
            <FormTextarea
              value={editingOption.prompt || editingOption.option || ''}
              onChange={(e) => setEditingOption({ ...editingOption, prompt: e.target.value })}
              placeholder="Enter prompt text"
              style={{ minHeight: '100px' }}
            />
          </FormGroup>
          <FormGroup>
            <FormLabel>Select Cluster</FormLabel>
            <FormSelect
              value={selectedClusterForEdit}
              onChange={(e) => setSelectedClusterForEdit(e.target.value)}
            >
              <option value="">-- Select Cluster --</option>
              {clusterList.map((c) => (
                <option key={c._id} value={c._id}>
                  {c.name}
                </option>
              ))}
            </FormSelect>
          </FormGroup>
          <div style={{ marginTop: '24px', paddingTop: '16px', borderTop: '1px solid #e5e7eb' }}>
            {editOptionClusters.length > 0 && (
              <div style={{ maxHeight: '200px', overflowY: 'auto', marginBottom: '12px' }}>
                {editOptionClusters.map((cluster, index) => (
                  <FormGroup key={index}>
                    <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-start' }}>
                      <FormSelect
                        value={cluster._id || ''}
                        onChange={(e) => handleUpdateClusterInEditOption(index, e.target.value)}
                        style={{ flex: 1 }}
                      >
                        <option value="">-- Select Cluster --</option>
                        {clusterList.map((c) => (
                          <option key={c._id} value={c._id}>
                            {c.name}
                          </option>
                        ))}
                      </FormSelect>
                      <button
                        type="button"
                        onClick={() => handleRemoveClusterFromEditOption(index)}
                        style={{
                          padding: '8px 12px',
                          background: '#ef4444',
                          color: '#fff',
                          border: 'none',
                          borderRadius: '8px',
                          cursor: 'pointer',
                          fontSize: '12px',
                        }}
                      >
                        Remove
                      </button>
                    </div>
                  </FormGroup>
                ))}
              </div>
            )}
            <button
              type="button"
              onClick={handleAddClusterToEditOption}
              style={{
                padding: '8px 12px',
                background: 'linear-gradient(90deg, #7b5cff, #60a5fa)',
                color: '#fff',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                fontSize: '12px',
                width: '100%',
              }}
            >
              + Add Cluster
            </button>
          </div>
          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowEditPromptsModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton onClick={handleSaveEditPrompts}>Save Prompt</PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Modal: Add Question ──────────────────────────────────────────────────
  const renderAddQuestionModal = () => {
    if (!showAddQuestionModal) return null;

    const isNQ = addQuestionData.flowType === 'NQ';
    const isICWithNewQuestion =
      addQuestionData.flowType === 'IC' && addQuestionData.questionName2 === '1';
    const showNewQuestionFields = isNQ || isICWithNewQuestion;
    const showValidationPrompt =
      showNewQuestionFields && isNonOptionType(addQuestionData.questionType);
    // File upload only shown for pure NQ flow
    const showFileUpload = isNQ;

    return (
      <Modal onClick={() => setShowAddQuestionModal(false)}>
        <ModalContent
          onClick={(e) => e.stopPropagation()}
          style={{ maxHeight: '90vh', overflowY: 'auto' }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '24px',
            }}
          >
            <ModalTitle>Add Question</ModalTitle>
            <button
              onClick={() => setShowAddQuestionModal(false)}
              style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            >
              <X size={20} />
            </button>
          </div>

          <FormGroup>
            <FormLabel>Flow Type *</FormLabel>
            <FormSelect
              value={addQuestionData.flowType}
              onChange={(e) => handleFlowTypeChange(e.target.value)}
            >
              {FLOW_TYPES.map((type) => (
                <option key={type.value} value={type.value}>
                  {type.label}
                </option>
              ))}
            </FormSelect>
          </FormGroup>

          {addQuestionData.flowType === 'RL' && (
            <FormGroup>
              <FormLabel>Select Question to Re-loop *</FormLabel>
              <FormSelect
                value={addQuestionData.questionName}
                onChange={(e) =>
                  setAddQuestionData({ ...addQuestionData, questionName: e.target.value })
                }
              >
                {questionList.map((q) => (
                  <option key={q.questionCode} value={q.questionCode}>
                    {q.title}
                  </option>
                ))}
              </FormSelect>
            </FormGroup>
          )}

          {addQuestionData.flowType === 'IC' && (
            <>
              <FormGroup>
                <FormLabel>Select Cluster *</FormLabel>
                <FormSelect
                  value={addQuestionData.clusterName}
                  onChange={(e) =>
                    setAddQuestionData({ ...addQuestionData, clusterName: e.target.value })
                  }
                >
                  <option value="">-- Select Cluster --</option>
                  {clusterList.map((c) => (
                    <option key={c._id} value={c._id}>
                      {c.name}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              <FormGroup>
                <FormLabel>Link to Existing Question or Create New *</FormLabel>
                <FormSelect
                  value={addQuestionData.questionName2}
                  onChange={(e) =>
                    setAddQuestionData({ ...addQuestionData, questionName2: e.target.value })
                  }
                >
                  <option value="null">NONE</option>
                  <option value="1">Add New Question</option>
                  {questionList.map((q) => (
                    <option key={q.questionCode} value={q.questionCode}>
                      {q.title}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
            </>
          )}

          {showNewQuestionFields && (
            <>
              <FormGroup>
                <FormLabel>Question Title *</FormLabel>
                <FormInput
                  value={addQuestionData.newQuestionTitle}
                  onChange={(e) =>
                    setAddQuestionData({ ...addQuestionData, newQuestionTitle: e.target.value })
                  }
                  placeholder="Enter question title"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Question *</FormLabel>
                <FormTextarea
                  value={addQuestionData.newQuestion}
                  onChange={(e) =>
                    setAddQuestionData({ ...addQuestionData, newQuestion: e.target.value })
                  }
                  placeholder="Enter question text"
                />
              </FormGroup>
              <FormGroup>
                <FormLabel>Question Type *</FormLabel>
                <FormSelect
                  value={addQuestionData.questionType}
                  onChange={(e) =>
                    setAddQuestionData({ ...addQuestionData, questionType: e.target.value })
                  }
                >
                  {QUESTION_TYPES.map((type) => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </FormSelect>
              </FormGroup>
              {showValidationPrompt && (
                <FormGroup>
                  <FormLabel>Validation Prompt</FormLabel>
                  <FormTextarea
                    value={addQuestionData.newValidationPrompt}
                    onChange={(e) =>
                      setAddQuestionData({
                        ...addQuestionData,
                        newValidationPrompt: e.target.value,
                      })
                    }
                    placeholder="Enter validation prompt"
                  />
                </FormGroup>
              )}
            </>
          )}

          {/* ── File Upload — only for NQ flow with existing documents ── */}
          {showFileUpload && (
            <div style={{ borderTop: '1px solid #e5e7eb', paddingTop: '16px', marginTop: '8px' }}>
              <FileUploadSection
                inputId="add-question-file-upload"
                uploadedFiles={addQuestionFiles.uploadedFiles}
                existingDocuments={addQuestionFiles.existingDocuments}
                isUploadingFile={addQuestionFiles.isUploadingFile}
                fileError={addQuestionFiles.fileError}
                isFileOperationLoading={addQuestionFiles.isFileOperationLoading}
                isLoading={false}
                handleFileUpload={addQuestionFiles.handleFileUpload}
                handleFileDelete={addQuestionFiles.handleFileDelete}
                handleFileView={addQuestionFiles.handleFileView}
                handleExistingDocumentDelete={addQuestionFiles.handleExistingDocumentDelete}
              />
            </div>
          )}

          <ModalButtonGroup>
            <SecondaryModalButton onClick={() => setShowAddQuestionModal(false)}>
              Cancel
            </SecondaryModalButton>
            <PrimaryModalButton
              onClick={handleSaveQuestion}
              disabled={addQuestionFiles.isFileOperationLoading || addQuestionFiles.isUploadingFile}
            >
              Submit
            </PrimaryModalButton>
          </ModalButtonGroup>
        </ModalContent>
      </Modal>
    );
  };

  // ─── Main Render ──────────────────────────────────────────────────────────
  return (
    <AdminLayout pageTitle="Cluster Questions" activeTab="ClusterQuestionsCreation">
      <Container>
        {renderEditQuestionModal()}
        {renderAddOptionModal()}
        {renderAddPromptsModal()}
        {renderEditOptionModal()}
        {renderEditPromptsModal()}
        {renderAddQuestionModal()}

        <HeaderSection>
          <div>
            <PageTitle>Questions Management</PageTitle>
            <p style={{ margin: '4px 0 0 0', fontSize: '14px', color: '#6b7280' }}>
              Cluster ID: {clusterId}
            </p>
          </div>
          <SaveButton onClick={handleSaveAll} disabled={saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving...' : 'Save All Questions'}
          </SaveButton>
        </HeaderSection>

        <QuestionTreeContainer>
          {isLoading ? (
            <LoadingContainer>
              <LoadingSpinner />
              <LoadingText>Loading questions...</LoadingText>
            </LoadingContainer>
          ) : isError ? (
            <ErrorContainer>Error loading questions. Please try again.</ErrorContainer>
          ) : questionList.length === 0 ? (
            <p style={{ textAlign: 'center', color: '#6b7280', padding: '40px 0' }}>
              No questions found. Add a new question to get started.
            </p>
          ) : (
            <div>{questionList?.[0] && renderQuestionNode(questionList[0])}</div>
          )}
        </QuestionTreeContainer>

        <HiddenJsonArea ref={jsonAreaRef} id="questionTreeJson" readOnly />
      </Container>
    </AdminLayout>
  );
};

export default ClusterQuestionsCreation;
